import React, { useEffect, useRef, useState } from 'react';
import {
  Avatar, Box, Button, Card, CardContent, Collapse,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Divider, IconButton, TextField, Tooltip, Typography,
} from '@mui/material';
import { Delete, Home, Refresh, Settings } from '@mui/icons-material';
import { useLanguage } from '../i18n/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Gateway } from '../types';
import { statesUrl, parseStates } from '../boardApi';

interface Props {
  gateway: Gateway;
  onSelect: () => void;
  onDelete?: () => void;
  onChange: (gateway: Gateway) => void;
  showSettings: boolean;
  onShowSettingsChange: (show: boolean) => void;
  loadDelay?: number;
}

const connectivityColor = (gw: Gateway): string => {
  if (!gw.lastFetchedAt) return '#9E9E9E';
  if (gw.nodes.some(n => n.error)) return '#EF5350';
  return '#4CAF50';
};

const connectivityLabel = (gw: Gateway, connected: string, unknown: string, error: string): string => {
  if (!gw.lastFetchedAt) return unknown;
  if (gw.nodes.some(n => n.error)) return error;
  return connected;
};

export const GatewayCard: React.FC<Props> = ({ gateway, onSelect, onDelete, onChange, showSettings, onShowSettingsChange, loadDelay = 0 }) => {
  const { t } = useLanguage();
  const { getAuthHeader } = useAuth();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const gatewayRef = useRef(gateway);
  gatewayRef.current = gateway;

  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  useEffect(() => {
    if (!gateway.url) return;
    if (gateway.lastFetchedAt && Date.now() - gateway.lastFetchedAt < 30_000) return;
    const controller = new AbortController();
    const stagger = setTimeout(() => {
      const gw = gatewayRef.current;
      if (!gw.url) return;
      const timer = setTimeout(() => controller.abort(), 8000);
      fetch(statesUrl(gw.url), { headers: getAuthHeader(), signal: controller.signal })
        .then(async response => {
          clearTimeout(timer);
          if (!response.ok) throw new Error();
          const html = await response.text();
          const states = parseStates(html);
          onChange({
            ...gw,
            lastFetchedAt: Date.now(),
            nodes: gw.nodes.map((n, i) => ({ ...n, state: i < states.length ? states[i] : null, error: undefined })),
          });
        })
        .catch(() => {
          clearTimeout(timer);
          onChange({
            ...gw,
            lastFetchedAt: undefined,
            nodes: gw.nodes.map(n => ({ ...n, state: null, error: t.errorConnection })),
          });
        });
    }, loadDelay);
    return () => { clearTimeout(stagger); controller.abort(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const scheduleUrlFetch = (url: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!url.trim()) return;
    debounceRef.current = setTimeout(async () => {
      const gw = gatewayRef.current;
      try {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 8000);
        const response = await fetch(statesUrl(url), { headers: getAuthHeader(), signal: controller.signal });
        clearTimeout(timer);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const html = await response.text();
        const states = parseStates(html);
        onChange({
          ...gw,
          lastFetchedAt: Date.now(),
          nodes: gw.nodes.map((n, i) => ({ ...n, state: i < states.length ? states[i] : null, error: undefined })),
        });
      } catch {
        onChange({
          ...gw,
          nodes: gw.nodes.map(n => ({ ...n, error: t.errorConnection })),
        });
      }
    }, 800);
  };

  return (
    <Card elevation={2} sx={{ mb: 2, border: '1px solid', borderColor: 'divider' }}>
      {/* Tapping anywhere on the card opens it, so the card itself has to be
          reachable and operable from a keyboard, not just a pointer. */}
      <CardContent
        role="button"
        tabIndex={0}
        aria-label={t.a11yOpenGateway(gateway.name)}
        sx={{ cursor: 'pointer' }}
        onClick={onSelect}
        onKeyDown={e => {
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); }
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" color="text.primary">
            {gateway.name}
          </Typography>
          <Box onClick={e => e.stopPropagation()}>
            <IconButton
              size="small"
              aria-label={t.a11yGatewaySettings}
              aria-expanded={showSettings}
              onClick={() => onShowSettingsChange(!showSettings)}
            >
              <Settings fontSize="small" />
            </IconButton>
            <Tooltip title={!onDelete ? t.gatewayDeleteOnlyOne : ''}>
              <span>
                <IconButton size="small" aria-label={t.gatewayDeleteButton} disabled={!onDelete} onClick={() => setConfirmDelete(true)}>
                  <Delete fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Avatar sx={{
            width: 56,
            height: 56,
            bgcolor: '#4CAF50',
            boxShadow: '0 4px 10px rgba(0,0,0,0.5)',
          }}>
            <Home sx={{ fontSize: 28 }} />
          </Avatar>
          <Box display="flex" alignItems="center" gap={0.75} onClick={e => e.stopPropagation()}>
            <IconButton size="small" aria-label={t.a11yRefresh} onClick={() => scheduleUrlFetch(gateway.url)} sx={{ p: 0.5, color: 'text.secondary' }}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: connectivityColor(gateway), flexShrink: 0 }} />
            <Typography variant="body2" sx={{ color: connectivityColor(gateway) }}>
              {connectivityLabel(gateway, t.gatewayStatusConnected, t.statusUnknown, t.errorConnection)}
            </Typography>
          </Box>
        </Box>
      </CardContent>
      <Collapse in={showSettings}>
        <Box sx={{ px: 2, pb: 2 }} onClick={e => e.stopPropagation()}>
          <TextField
            fullWidth size="small" label={t.gatewayNameLabel}
            value={gateway.name}
            onChange={e => onChange({ ...gateway, name: e.target.value })}
            sx={{ mb: 1 }}
          />
          <TextField
            fullWidth size="small" label={t.gatewayUrlLabel}
            placeholder={t.gatewayUrlPlaceholder}
            value={gateway.url}
            onChange={e => {
              const url = e.target.value;
              onChange({
                ...gateway,
                url,
                lastFetchedAt: undefined,
                nodes: gateway.nodes.map(n => ({ ...n, state: null, error: undefined })),
              });
              scheduleUrlFetch(url);
            }}
            sx={{ mb: 1 }}
          />
        </Box>
      </Collapse>
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>{gateway.name}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t.gatewayDeleteConfirm}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>{t.nodeEditCancel}</Button>
          <Button color="error" onClick={() => { setConfirmDelete(false); onDelete?.(); }}>{t.gatewayDeleteButton}</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};
